import { SampleFile, TextFile } from "projen";
import { merge } from "ts-deepmerge";
import { resolveReactViteSiteProjectOutdir } from "./resolve-outdir";
import {
  TestRunner,
  TypeScriptProject,
  TypeScriptProjectOptions,
} from "./typescript-project";
import { TurboRepo } from "../turbo";

/**
 * Configuration options for {@link ReactViteSiteProject}.
 *
 * Inherits every option from {@link TypeScriptProjectOptions}. Additional
 * deps the caller wants on top of the baked-in React / Vite / Tailwind /
 * Vitest stack go through the standard `deps` / `devDeps` arrays — they
 * merge with the project's defaults rather than replacing them.
 */
export interface ReactViteSiteProjectOptions extends TypeScriptProjectOptions {
  /**
   * Emit minimal React + Vite starter files (`index.html`, `src/main.tsx`,
   * `src/App.tsx`, `src/routes.tsx`, `src/index.css`, `src/setupTests.ts`).
   *
   * @default false
   */
  readonly sampleCode?: boolean;
}

/**
 * Vite + React 19 + Tailwind v4 + Vitest site scaffolded on top of
 * {@link TypeScriptProject}.
 *
 * Replaces the inherited `tsc` compile step with `vite build`, wires
 * Vitest as the test runner (with jsdom + Testing Library defaults),
 * registers ESLint rules for React, applies team-friendly VSCode
 * defaults, and pins Node via `.nvmrc`. Defaults `outdir` to
 * `sites/<scope>/<name>` per the monorepo-layout contract (ADR-006).
 *
 * The Tailwind v4 integration goes through `@tailwindcss/vite` — no
 * PostCSS pipeline — matching the canonical CodeDrifters React-site
 * scaffold.
 *
 * @see ./react-vite-site-project.ts — implementation
 * @see ./add-storybook.ts — opt-in Storybook add-on
 * @see ./add-playwright.ts — opt-in Playwright E2E add-on
 */
export class ReactViteSiteProject extends TypeScriptProject {
  constructor(userOptions: ReactViteSiteProjectOptions) {
    /*************************************************************************
     *
     * Resolve outdir
     *
     * When the caller omits `outdir`, derive it from the scoped package
     * name (`packageName ?? name`) as `sites/<scope>/<name>`. This
     * enforces the monorepo-layout contract (ADR-006) without requiring
     * every React-site sub-project to restate its own path. Resolution
     * runs before `super()` so the derived value is passed explicitly —
     * otherwise the TypeScriptProject base class would default it to
     * `packages/<scope>/<name>` instead.
     *
     ************************************************************************/

    const resolvedOutdir =
      userOptions.outdir ??
      resolveReactViteSiteProjectOutdir(
        userOptions.packageName ?? userOptions.name,
      );

    /**
     * React sites consume Vitest as their test runner (Jest does not
     * cooperate with Vite's ESM-only config) and never expose a library
     * API surface, so the inherited `@microsoft/api-extractor` wiring
     * is opt-in for these projects. Callers can flip
     * `apiExtractor: { ... }` back on if they genuinely re-export a
     * typed surface.
     *
     * Use `ts-deepmerge`'s `merge()` (not a shallow spread) so a default
     * nested object like
     * `tsconfig: { compilerOptions: { declaration: false } }` survives a
     * user override that only sets unrelated tsconfig fields.
     */
    const defaultOptions: Partial<ReactViteSiteProjectOptions> = {
      testRunner: TestRunner.VITEST,
      apiExtractor: false,
      // ESLint inherited from TypeScriptProject lints `src` only. projen 0.100
      // removed the `lintProjenRc` option and no longer auto-lints the projenrc
      // file, so the previous explicit opt-out is unnecessary — downstream
      // sites configure projen through their own root project.
      eslintOptions: {
        dirs: ["src"],
      },
      // VSCode workspace defaults (Prettier formatter, ESLint on save)
      // need a project-level `.vscode/` folder. The inherited
      // TypeScriptProject does not enable it on sub-projects by
      // default; flip it on so the React-site VSCode wiring below
      // actually renders.
      vscode: true,
      tsconfig: {
        compilerOptions: {
          declaration: false,
        },
      },
    };
    const merged = merge(
      defaultOptions,
      userOptions,
    ) as ReactViteSiteProjectOptions;
    const options: ReactViteSiteProjectOptions = {
      ...merged,
      outdir: resolvedOutdir,
    };

    super(options);

    /**
     * Vite + Tailwind v4 ships as ESM; the site package must be a module.
     */
    this.package.addField("type", "module");

    /**
     * Pin the Node.js version for local development. Mirrors the
     * `.nvmrc` the monorepo root carries — keeps a fresh checkout's
     * locally installed Node matched to whatever projen synthesises
     * against in CI.
     */
    new TextFile(this, ".nvmrc", { lines: ["v24.11.0"] });

    /**
     * Vite-friendly TypeScript config. The inherited TypeScriptProject
     * config is tuned for library output (`declaration: true`, `outDir`,
     * `rootDir`) which conflicts with Vite's bundler-mode resolution.
     * Override the projen-managed `tsconfig.json` in place — addOverride
     * replaces values cleanly without fighting projen's file registry.
     */
    this.tsconfig?.file.addOverride("compilerOptions.target", "ES2020");
    this.tsconfig?.file.addOverride("compilerOptions.lib", [
      "ES2020",
      "DOM",
      "DOM.Iterable",
    ]);
    this.tsconfig?.file.addOverride("compilerOptions.module", "ESNext");
    this.tsconfig?.file.addOverride(
      "compilerOptions.moduleResolution",
      "bundler",
    );
    this.tsconfig?.file.addOverride("compilerOptions.jsx", "react-jsxdev");
    this.tsconfig?.file.addOverride("compilerOptions.noEmit", true);
    this.tsconfig?.file.addOverride("compilerOptions.skipLibCheck", true);
    this.tsconfig?.file.addOverride("compilerOptions.strict", true);
    this.tsconfig?.file.addOverride(
      "compilerOptions.noUncheckedIndexedAccess",
      true,
    );
    this.tsconfig?.file.addOverride("compilerOptions.noImplicitOverride", true);
    this.tsconfig?.file.addOverride(
      "compilerOptions.exactOptionalPropertyTypes",
      true,
    );
    this.tsconfig?.file.addOverride("compilerOptions.resolveJsonModule", true);
    this.tsconfig?.file.addOverride("compilerOptions.declaration", false);
    this.tsconfig?.file.addOverride("compilerOptions.paths", {
      "@/*": ["./src/*"],
    });
    this.tsconfig?.file.addOverride("include", ["src"]);

    /**
     * Pull the vitest/vite type roots into the main tsconfig. projen
     * 0.100.4+'s typescript-eslint project service resolves co-located
     * `src/**` `.tsx` test files to this config (not the test-dir
     * tsconfig), so the test-runner globals must live here for ESLint to
     * type-check them cleanly. `include: ["src"]` above already covers
     * those files (TypeScriptProject no longer excludes test files).
     */
    this.tsconfig?.file.addOverride("compilerOptions.types", [
      "vitest/globals",
      "vite/client",
    ]);

    /**
     * Vite config: Tailwind v4 plugin loaded before React (matches the
     * canonical example in Tailwind's docs) and an `@/*` path alias
     * pointed at `./src`.
     */
    new SampleFile(this, "vite.config.ts", {
      contents: `import { defineConfig } from 'vite';
import react from '@vitejs/plugin-react';
import tailwindcss from '@tailwindcss/vite';
import path from 'path';

export default defineConfig({
  plugins: [tailwindcss(), react()],
  resolve: { alias: { '@': path.resolve(__dirname, 'src') } },
});
`,
    });

    /**
     * `src/vite-env.d.ts` — typed `ImportMetaEnv` stub so sites have a
     * declared home for their `VITE_*` env vars and `import.meta.env`
     * accesses type-check. Emitted unconditionally via `SampleFile` so
     * sites without `sampleCode: true` still get the template; the
     * write-once semantics protect any edits a downstream site has
     * already made.
     */
    new SampleFile(this, "src/vite-env.d.ts", {
      contents: `/// <reference types="vite/client" />

interface ImportMetaEnv {
  // Declare your VITE_* env vars here, e.g.
  // readonly VITE_API_BASE_URL: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}
`,
    });

    /**
     * Vitest config: extends Vite config so plugins (React, Tailwind)
     * apply during tests, and sets jsdom + globals + Testing Library
     * setup file. The inherited TypeScriptProject's `Vitest` component
     * already emitted a baseline `vitest.config.ts` from `super()`; we
     * remove it and write our own so Vite plugins propagate into the
     * test environment. Skipped when the caller selected Jest — in
     * that case the project has no Vitest config at all and the file
     * stays absent.
     */
    if (options.testRunner !== TestRunner.JEST) {
      this.tryRemoveFile("vitest.config.ts");
      new SampleFile(this, "vitest.config.ts", {
        contents: `import { defineConfig, mergeConfig } from 'vitest/config';
import react from '@vitejs/plugin-react';
import viteConfig from './vite.config';

export default mergeConfig(
  viteConfig,
  defineConfig({
    plugins: [react()],
    test: {
      environment: 'jsdom',
      globals: true,
      setupFiles: ['./src/setupTests.ts'],
      passWithNoTests: true,
    },
  })
);
`,
      });
    }

    /**
     * Replace projen's tsc-driven compile with `vite build`. Sites are
     * not npm tarballs — neutralise `package` too so the inherited
     * `build` task doesn't emit `dist/js/*.tgz` alongside the site
     * output.
     */
    this.compileTask.reset();
    this.compileTask.exec("vite build");
    this.packageTask.reset();

    /**
     * Point `watch` at Vite's dev server (projen's default runs `tsc -w`,
     * which is meaningless for a Vite site).
     */
    this.tasks.tryFind("watch")?.reset("vite");

    /**
     * Vite / Vitest lifecycle tasks. Add only the ones projen does not
     * already provide.
     */
    if (!this.tasks.tryFind("dev")) {
      this.addTask("dev", {
        description: "Start the Vite dev server",
        exec: "vite",
        receiveArgs: true,
      });
    }
    if (!this.tasks.tryFind("preview")) {
      this.addTask("preview", {
        description: "Preview the built site locally",
        exec: "vite preview",
        receiveArgs: true,
      });
    }

    /**
     * Runtime React + router deps.
     */
    this.addDeps("react", "react-dom", "react-router-dom");

    /**
     * Dev deps: Vite + Tailwind plugin, Vitest + Testing Library,
     * React type defs, and ESLint plugins for React.
     */
    this.addDevDeps(
      "vite",
      "@vitejs/plugin-react",
      "@tailwindcss/vite",
      "tailwindcss",
      "jsdom",
      "@testing-library/react",
      "@testing-library/jest-dom",
      "@testing-library/user-event",
      "@types/react",
      "@types/react-dom",
      "eslint-plugin-react",
      "eslint-plugin-react-hooks",
      "eslint-plugin-jsx-a11y",
    );

    /**
     * Wire ESLint to lint React JSX. The base TypeScriptProject already
     * enables `eslint`; we layer on the React plugins / configs and the
     * settings to detect the installed React version automatically.
     */
    this.eslint?.addExtends("plugin:react/recommended");
    this.eslint?.addExtends("plugin:react/jsx-runtime");
    this.eslint?.addExtends("plugin:react-hooks/recommended");
    this.eslint?.addExtends("plugin:jsx-a11y/recommended");
    this.eslint?.addOverride({
      files: ["**/*.{tsx,jsx}"],
      // Projen's ESLint override type does not include `settings`, but
      // `settings` is a valid ESLint override key. Cast to `any` so the
      // generated `.eslintrc.json` carries the React-version detection
      // settings on the JSX glob.
      settings: {
        react: {
          version: "detect",
        },
      },
    } as any);
    this.eslint?.addRules({
      // We use TypeScript; prop-types are unnecessary.
      "react/prop-types": "off",
      // New JSX transform — React no longer needs to be in scope.
      "react/react-in-jsx-scope": "off",
    });

    /**
     * Test files import dev-only assertion helpers (e.g.
     * `@testing-library/jest-dom`). Loosen
     * `import/no-extraneous-dependencies` for tests and the Vitest
     * setup file so ESLint does not flag them.
     */
    this.eslint?.addOverride({
      files: [
        "**/*.{test,spec}.ts",
        "**/*.{test,spec}.tsx",
        "**/__tests__/**",
        "src/setupTests.ts",
      ],
      rules: {
        "import/no-extraneous-dependencies": [
          "error",
          { devDependencies: true },
        ],
      },
    });

    /**
     * VSCode workspace defaults — Prettier as the default formatter,
     * ESLint + organizeImports on save for TS/TSX/JS/JSX, and
     * recommended extensions.
     */
    this.vscode?.settings.addSettings({
      "editor.defaultFormatter": "esbenp.prettier-vscode",
      "editor.formatOnSave": true,
      // Prettier must format the whole file; "modifications" mode is
      // known to mis-merge ranges with Prettier and can corrupt saves.
      "editor.formatOnSaveMode": "file",
      "prettier.requireConfig": true,

      "eslint.useFlatConfig": false,
      "eslint.workingDirectories": [{ directory: ".", changeProcessCWD: true }],
      // Prettier is the default formatter; ESLint's fixAll must not
      // re-apply prettier/prettier (would double-format).
      "eslint.codeActionsOnSave.rules": ["!prettier/prettier", "*"],
      "eslint.format.enable": false,

      "[typescript]": {
        "editor.codeActionsOnSave": {
          "source.fixAll.eslint": "always",
          "source.organizeImports": "always",
        },
      },
      "[typescriptreact]": {
        "editor.codeActionsOnSave": {
          "source.fixAll.eslint": "always",
          "source.organizeImports": "always",
        },
      },
      "[javascript]": {
        "editor.codeActionsOnSave": {
          "source.fixAll.eslint": "always",
        },
      },
      "[javascriptreact]": {
        "editor.codeActionsOnSave": {
          "source.fixAll.eslint": "always",
        },
      },

      "eslint.validate": [
        "javascript",
        "javascriptreact",
        "typescript",
        "typescriptreact",
      ],
    });

    this.vscode?.extensions.addRecommendations("dbaeumer.vscode-eslint");
    this.vscode?.extensions.addRecommendations("esbenp.prettier-vscode");

    /**
     * Extend turbo inputs so the compile task invalidates on Vite-
     * specific source paths. Outputs (`dist/**`) are already added by
     * the base class.
     */
    const turbo = TurboRepo.of(this);
    if (turbo?.compileTask) {
      turbo.compileTask.inputs.push(
        "public/**",
        "index.html",
        "vite.config.ts",
      );
    }

    /**
     * Optional starter files. Matches the
     * `createBaseReactSite` reference shape — minimal but runnable.
     */
    if (userOptions.sampleCode === true) {
      const siteName = options.name;
      new SampleFile(this, "index.html", {
        contents: `<!doctype html>
<html>
  <head>
    <meta charset="utf-8"/>
    <meta name="viewport" content="width=device-width, initial-scale=1"/>
    <title>${siteName}</title>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>
`,
      });
      new SampleFile(this, "src/routes.tsx", {
        contents: `import { createBrowserRouter } from 'react-router-dom';
import App from './App';

export const router = createBrowserRouter([
  { path: '/', element: <App /> },
]);
`,
      });
      new SampleFile(this, "src/main.tsx", {
        contents: `import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from './routes';
import './index.css';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);
`,
      });
      new SampleFile(this, "src/App.tsx", {
        contents: `export default function App() {
  return (
    <main className="p-4">
      <h1 className="text-2xl font-bold">Hello from ${siteName}</h1>
    </main>
  );
}
`,
      });
      new SampleFile(this, "src/index.css", {
        contents: `@import "tailwindcss";
`,
      });
      new SampleFile(this, "src/setupTests.ts", {
        contents: `import '@testing-library/jest-dom';
`,
      });
    }
  }
}
